"use client"

import { useFormikContext } from "formik"

import { NovaPostSenderLocationSearchField } from "@/components/deliveries/providers/nova-post/nova-post-sender-location-search-field"
import { NovaPostSenderProfileField } from "@/components/deliveries/providers/nova-post/nova-post-sender-profile-field"
import { FormInput, FormSelect } from "@/components/form-inputs"
import type { DeliveryIntegrationConfig } from "@/lib/deliveries/config"
import { useTranslations } from "next-intl"



export function NovaPostIntegrationFormFields({
  apiKeyMode,
  integrationId,
}: {
  apiKeyMode: "required" | "optional-replacement"
  integrationId?: string
}) {

  const tUi = useTranslations("Deliveries.DeliveriesProvidersNovaPostNovaPostIntegrationFormFields")

  const { values } = useFormikContext<{
    apiKey: string
    config: DeliveryIntegrationConfig
  }>()
  const replacesExistingKey = apiKeyMode === "optional-replacement"

  return (
    <div className="min-w-0 space-y-4">
      <FormInput
        name="name"
        label={tUi("integrationName")}
        maxLength={100}
        required
      />
      <FormInput
        name="apiKey"
        label={replacesExistingKey ? tUi("newAPIKeyOptional") : tUi("aPIKey")}
        type="password"
        autoComplete="new-password"
        required={!replacesExistingKey}
        description={replacesExistingKey
          ? tUi("leaveBlankToKeepTheExistingTenantSpecific")
          : tUi("useTheAPIKeyCreatedInYourNova")}
      />
      <NovaPostSenderProfileField
        apiKey={values.apiKey}
        providerConfig={values.config.providerConfig}
        integrationId={integrationId}
      />
      <NovaPostSenderLocationSearchField
        apiKey={values.apiKey}
        providerConfig={values.config.providerConfig}
        integrationId={integrationId}
      />
      <FormSelect
        name="config.defaults.deliveryPaymentPayer"
        label={tUi("defaultDeliveryFeePayer")}
        options={[
          { value: "sender", label: tUi("merchantSender") },
          { value: "recipient", label: tUi("customerRecipient") },
          { value: "third_party", label: tUi("thirdParty") },
        ]}
      />
      <NovaPostPayerContractField
        hasContract={Boolean(values.config.defaults.payerContractReference)}
        payer={values.config.defaults.deliveryPaymentPayer}
      />
    </div>
  )
}

function NovaPostPayerContractField({
  hasContract,
  payer,
}: {
  hasContract: boolean
  payer: DeliveryIntegrationConfig["defaults"]["deliveryPaymentPayer"]
}) {
  const tUi = useTranslations("Deliveries.DeliveriesProvidersNovaPostNovaPostIntegrationFormFields")

  switch (payer) {
    case "third_party":
      return (
        <FormInput
          name="config.defaults.payerContractReference"
          label={tUi("thirdPartyPayerContractNumberOrEDRPOU")}
          required
          description={tUi("novaPostRequiresTheThirdPartyPayerS")}
        />
      )
    case "sender":
    case "recipient":
      return (
        <details
          className="rounded-md border px-4 py-3"
          open={hasContract || undefined}
        >
          <summary className="cursor-pointer text-sm font-medium">
            <span key={tUi("advancedDeliveryFeeBilling")}>{tUi("advancedDeliveryFeeBilling")}</span></summary>
          <div className="mt-3">
            <FormInput
              name="config.defaults.payerContractReference"
              label={tUi("payerContractNumberOptional")}
              description={tUi("useOnlyForNovaPostContractualNonCash")}
            />
          </div>
        </details>
      )
    default: {
      const exhaustivePayer: never = payer
      return exhaustivePayer
    }
  }
}
